import { Box, Tooltip } from '@mui/material';
import { t } from '@i18n';
import { Chip } from '@molecules';
import { formatDate, parseAmount } from '@utils';
import { PendingDispersion, PendingDispersionStatus } from './data/useGetPendingDispersions';
import { SelectedPendingDispersion } from './data/useResolvePendingDispersion/hooks/useResolvePendingDispersion';
import ActionButtons from './modules/ActionButtons';
import styles from './styles';

export const DEFAULT_ROWS_PER_PAGE = 10;

export const STATUS_OPTIONS: { value: PendingDispersionStatus; label: string }[] = [
  { value: 'PENDING', label: 'PENDING_DISPERSIONS:STATUS:PENDING' },
  { value: 'APPROVED', label: 'PENDING_DISPERSIONS:STATUS:APPROVED' },
  { value: 'REJECTED', label: 'PENDING_DISPERSIONS:STATUS:REJECTED' },
];

const STATUS_CHIP: Record<
  PendingDispersionStatus,
  { label: string; color: 'default' | 'success' | 'error' }
> = {
  PENDING: {
    label: 'PENDING_DISPERSIONS:STATUS:PENDING',
    color: 'default',
  },
  APPROVED: {
    label: 'PENDING_DISPERSIONS:STATUS:APPROVED',
    color: 'success',
  },
  REJECTED: {
    label: 'PENDING_DISPERSIONS:STATUS:REJECTED',
    color: 'error',
  },
};

/**
 * Pending dispersions table columns
 */
export const pendingDispersionsColumns = [
  {
    title: 'COMMON:COMPANY',
    content: ({ dispersion }: { dispersion: PendingDispersion }) => {
      const companyName = dispersion.companyName || '-';
      const isMaxCharacter = companyName.length > 20;
      return (
        <Tooltip title={isMaxCharacter && companyName} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{companyName}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'PENDING_DISPERSIONS:USERS_COUNT',
    content: ({ dispersion }: { dispersion: PendingDispersion }) => dispersion.usersCount ?? '-',
  },
  {
    title: 'DISPERSIONS:DISPERSION_DATE',
    content: ({ dispersion }: { dispersion: PendingDispersion }) => {
      const scheduledDate =
        (dispersion.scheduledDate && formatDate(dispersion.scheduledDate)) ||
        t('DISPERSIONS:INSTANT');
      const isMaxCharacter = scheduledDate.length > 15;
      return (
        <Tooltip title={isMaxCharacter && scheduledDate} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{scheduledDate}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'PENDING_DISPERSIONS:TOTAL_AMOUNT',
    content: ({ dispersion }: { dispersion: PendingDispersion }) =>
      parseAmount(dispersion.attemptedAmount),
  },
  {
    title: 'COMMON:STATUS',
    content: ({ dispersion }: { dispersion: PendingDispersion }) => {
      const statusProps = STATUS_CHIP[dispersion.status];
      if (!statusProps) return '-';
      return <Chip {...statusProps} variant='outlined' sx={styles.chip} />;
    },
  },
  {
    title: 'COMMON:CREATED_BY',
    content: ({ dispersion }: { dispersion: PendingDispersion }) => {
      const createdBy = dispersion.createdBy || '-';
      const isMaxCharacter = createdBy.length > 20;
      return (
        <Tooltip title={isMaxCharacter && createdBy} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{createdBy}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:CREATED_AT',
    content: ({ dispersion }: { dispersion: PendingDispersion }) => {
      const createdAt = (dispersion.createdAt && formatDate(dispersion.createdAt)) || '-';
      const isMaxCharacter = createdAt.length > 15;
      return (
        <Tooltip title={isMaxCharacter && createdAt} arrow>
          <Box sx={[styles.rowEllipsis, styles.nameMaxWidth]}>{createdAt}</Box>
        </Tooltip>
      );
    },
  },
  {
    title: 'COMMON:ACTIONS',
    content: ({
      dispersion,
      callback,
    }: {
      dispersion: PendingDispersion;
      callback: (d: SelectedPendingDispersion) => void;
    }) => <ActionButtons {...{ dispersion, callback }} />,
  },
];
